import { isEmpty } from '@ember/utils';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';

export function compareNumbers(valueA, valueB, isAscending) {
  if (valueA < valueB) {
    return isAscending ? -1 : 1;
  }

  if (valueA > valueB) {
    return isAscending ? 1 : -1;
  }

  return 0;
}

export function compareStrings(valueA, valueB, isAscending) {
  if (valueA.localeCompare(valueB) < 0) {
    return isAscending ? -1 : 1;
  }

  if (valueA.localeCompare(valueB) > 0) {
    return isAscending ? 1 : -1;
  }

  return 0;
}

export function compareLOSBucket(segmentationA, segmentationB, isAscending) {
  function getMinimumLengthOfStay(segmentation) {
    const [minimum /*, maximum */] = segmentation.segmentationValue
      .replace(/^revenue_insight\.los\.days\./, '')
      .split('.');

    return parseInt(minimum, 10);
  }

  const valueA = getMinimumLengthOfStay(segmentationA);
  const valueB = getMinimumLengthOfStay(segmentationB);

  return compareNumbers(valueA, valueB, isAscending);
}

export function compareNameAsNumber(segmentationA, segmentationB, isAscending) {
  const valueA = parseInt(segmentationA.segmentationValue, 10);
  const valueB = parseInt(segmentationB.segmentationValue, 10);

  return compareNumbers(valueA, valueB, isAscending);
}

export function comparePickupCount(segmentationA, segmentationB, isAscending) {
  const valueA = parseInt(segmentationA.pickupCount, 10);
  const valueB = parseInt(segmentationB.pickupCount, 10);

  return compareNumbers(valueA, valueB, isAscending);
}

export function compareWeekday(segmentationA, segmentationB, isAscending) {
  const weekdayOrder = {
    'parent-weekday': 12,
    'parent-weekend': 11,
    7: 10,
    1: 9,
    2: 8,
    3: 7,
    4: 6,
    5: 5,
    6: 4,
  };

  const valueA = weekdayOrder[segmentationA.segmentationSortValue] || 0;
  const valueB = weekdayOrder[segmentationB.segmentationSortValue] || 0;

  return compareNumbers(valueA, valueB, isAscending);
}

export function compareLeadTime(segmentationA, segmentationB, isAscending) {
  // id should look like this: (parent-)booking_lead_time-revenue_insight.booking_lead_time.<week|days>.<start>.(<end>|up)
  const splitValuesA = segmentationA.id.split('.');
  const splitValuesB = segmentationB.id.split('.');

  // Special case for "Not specified"
  if (splitValuesA.length === 2) {
    return 1;
  }

  if (splitValuesB.length === 2) {
    return -1;
  }

  let valueA = parseInt(splitValuesA[3], 10);
  let valueB = parseInt(splitValuesB[3], 10);

  // If the start value is different, sort by that
  const sortValue = compareNumbers(valueA, valueB, isAscending);
  if (sortValue !== 0) {
    return sortValue;
  }

  // Otherwise check if both have an end value
  if (splitValuesA.length > 4 && splitValuesB.length > 4) {
    valueA = splitValuesA[4];
    valueB = splitValuesB[4];

    if (
      Number.isNaN(parseInt(valueA, 10)) ||
      Number.isNaN(parseInt(valueB, 10))
    ) {
      return compareStrings(valueA, valueB, isAscending);
    }

    return compareNumbers(
      parseInt(valueA, 10),
      parseInt(valueB, 10),
      isAscending,
    );
  }

  // If the end value is missing, return the one with the longest length as the last
  return compareNumbers(splitValuesA.length, splitValuesB.length, isAscending);
}

/**
 * Always make sure the aggregated row is at the bottom
 * @param { object } a : a segmentation object
 * @param { object } b : another segmentation object
 * @return { number|null } : if there is an aggregated row we return a number and the sort function will immediately return that, or null
 */
function handleAggregatedRow(a, b) {
  if (a.isAggregatedRow) {
    return 1;
  }

  if (b.isAggregatedRow) {
    return -1;
  }

  return null;
}

/**
 * Sort function for sorting on the change segmentation values (we want to move the 0-0-0 columns down because they are less interesting)
 * @param  { array } values                        : array to sort
 * @param  { string } kpi                          : what the currently selected kpi is (nrOfRooms, adr, ....)
 * @param  { string } type                         : bob, change or reference
 * @param  { string } order                        : asc, or desc
 * @return { array }                               : sorted array
 */
export function sortSegmentationValueChange(values, kpi, type, order = 'asc') {
  return [...values].sort((a, b) => {
    const isAggregated = handleAggregatedRow(a, b);
    if (isAggregated !== null) {
      return isAggregated;
    }

    const valueA = a.calculatedValues[type][kpi];
    const currentValueA = a.calculatedValues.BOB[kpi];
    const refValueA = a.calculatedValues.reference[kpi];

    const valueB = b.calculatedValues[type][kpi];
    const currentValueB = b.calculatedValues.BOB[kpi];
    const refValueB = b.calculatedValues.reference[kpi];

    if (kpi === KPIS.ADR) {
      // Generic sort function that is used to compare values (change, currentValue or refValue)
      const sortValues = (sortValueA, sortValueB) => {
        if (sortValueA === sortValueB) {
          return 0;
        }
        if (isEmpty(sortValueA)) {
          return 1;
        }
        if (isEmpty(sortValueB)) {
          return -1;
        }
        if (order === 'asc') {
          return sortValueA < sortValueB ? -1 : 1;
        }

        return sortValueA < sortValueB ? 1 : -1;
      };

      let sortValue = sortValues(valueA, valueB);
      if (sortValue !== 0) {
        return sortValue;
      }

      sortValue = sortValues(currentValueA, currentValueB);
      if (sortValue !== 0) {
        return sortValue;
      }

      sortValue = sortValues(refValueA, refValueB);
      if (sortValue !== 0) {
        return sortValue;
      }

      return sortValue;
    }

    const hasValuesA = valueA || currentValueA || refValueA;
    const hasValuesB = valueB || currentValueB || refValueB;

    if (!hasValuesA) {
      return 1;
    }
    if (!hasValuesB) {
      return -1;
    }
    if (valueA === valueB) {
      return 0;
    }
    if (order === 'asc') {
      return valueA < valueB ? -1 : 1;
    }

    return valueA < valueB ? 1 : -1;
  });
}

/**
 * Sort function for sorting on the selected performance metric
 * Checks properties in the following order : selected, BOB, change, unfiltered data
 * @param  { array } values                              : array to sort
 * @param  { string } kpi                                : what the currently selected kpi is (nrOfRooms, adr, ....)
 * @param  { string } containsSelectedSegmentationValue  : array of selected segmentations
 * @param  { string } order                              : asc, or desc
 * @return { array }                                     : sorted array
 */
// eslint-disable-next-line max-params
export function sortSegmentationValueByKpi(
  values,
  kpi,
  containsSelectedSegmentationValue,
  type = 'BOB',
  order = 'desc',
) {
  // determines order, either asc or desc
  const orderNumber = order === 'asc' ? -1 : 1;

  return [...values].sort((a, b) => {
    const isAggregated = handleAggregatedRow(a, b);
    if (isAggregated !== null) {
      return isAggregated;
    }

    let valueA = a.calculatedValues[type][kpi];
    let valueB = b.calculatedValues[type][kpi];

    // Test: we want to show negative values before the 0 values. In this case users first see all the values that had impact
    if (!valueA) {
      valueA = -Infinity;
    }
    if (!valueB) {
      valueB = -Infinity;
    }

    const changeA = Math.abs(a.calculatedValues.change[kpi]);
    const changeB = Math.abs(b.calculatedValues.change[kpi]);

    const unfilteredValueA = !isEmpty(a.unfiltered_data)
      ? a.unfiltered_data.calculatedValues[type][kpi]
      : 0;
    const unfilteredValueB = !isEmpty(b.unfiltered_data)
      ? b.unfiltered_data.calculatedValues[type][kpi]
      : 0;

    const budgetValueA = a.calculatedValues.budget[kpi]
      ? a.calculatedValues.budget[kpi]
      : 0;
    const budgetValueB = b.calculatedValues.budget[kpi]
      ? b.calculatedValues.budget[kpi]
      : 0;

    const forecastValueA = a.calculatedValues.forecast[kpi]
      ? a.calculatedValues.forecast[kpi]
      : 0;
    const forecastValueB = b.calculatedValues.forecast[kpi]
      ? b.calculatedValues.forecast[kpi]
      : 0;

    const isSelectedA =
      containsSelectedSegmentationValue &&
      containsSelectedSegmentationValue.indexOf(a.segmentationSortValue) !== -1;
    const isSelectedB =
      containsSelectedSegmentationValue &&
      containsSelectedSegmentationValue.indexOf(b.segmentationSortValue) !== -1;

    const sortA = isSelectedA ? 0 : 1;
    const sortB = isSelectedB ? 0 : 1;

    let result;

    if (sortA !== sortB) {
      result = sortA - sortB;
    } else if (valueA !== valueB) {
      result = valueB - valueA;
    } else if (changeA !== changeB) {
      result = changeB - changeA;
    } else if (unfilteredValueA !== unfilteredValueB) {
      result = unfilteredValueB - unfilteredValueA;
    } else if (budgetValueA !== budgetValueB) {
      result = budgetValueB - budgetValueA;
    } else {
      result = forecastValueB - forecastValueA;
    }

    return result * orderNumber;
  });
}

/**
 * Sort function for numerical sorting in the pickup block (needs to check different properties)
 * @param  { array } values                              : array to sort
 * @param  { string } kpi                                : what the currently selected kpi is (nrOfRooms, adr, ....)
 * @param  { string } type                               : bob, change or reference
 * @param  { string } order                              : asc, or desc
 * @return { array }                                     : sorted array
 */
export function sortSegmentationNamePickup(values, kpi, type, order = 'asc') {
  return [...values].sort((a, b) => {
    const valueA = a.pickupCount;
    const valueB = b.pickupCount;

    if (order === 'asc') {
      return valueA - valueB;
    }
    return valueB - valueA;
  });
}

/**
 * Sort function for sorting based on booking_lead_time
 * @param  { array } values                              : array to sort
 * @param  { string } kpi                                : what the currently selected kpi is (nrOfRooms, adr, ....)
 * @param  { string } type                               : bob, change or reference
 * @param  { string } order                              : asc, or desc
 * @return { array }                                     : sorted array
 */
export function sortSegmentationNameLeadtime(values, kpi, type, order = 'asc') {
  return [...values].sort((a, b) => {
    // Leadtime will be sorted by id
    // It should look like this: (parent-)booking_lead_time-revenue_insight.booking_lead_time.<week|days>.<start>.(<end>)
    const sortValueA = a.id;
    const sortValueB = b.id;

    // determines order, either asc or desc
    const orderNumber = order === 'asc' ? 1 : -1;

    // afterwards split the ids.
    const splitValuesA = sortValueA.split('.');
    const splitValuesB = sortValueB.split('.');

    // Special case for "Not specified"
    // TODO: remove once API is fixed #ri-cancellations
    if (splitValuesA.length === 2) {
      return orderNumber;
    }

    if (splitValuesB.length === 2) {
      return -1 * orderNumber;
    }

    // first up is the start, these should both be ints
    let valueA = parseInt(splitValuesA[3], 10);
    let valueB = parseInt(splitValuesB[3], 10);

    // Same? one deeper
    if (valueA - valueB === 0) {
      // Possible that end is missing, if so return the one with the longest length as the last
      if (splitValuesA.length > 4 && splitValuesB.length > 4) {
        // If there is a fifth value its either an int or 'up'
        valueA = splitValuesA[4];
        valueB = splitValuesB[4];
        // Check if both are ints
        // if not, just use localecompare
        if (
          Number.isNaN(parseInt(valueA, 10)) ||
          Number.isNaN(parseInt(valueB, 10))
        ) {
          return valueA.localeCompare(valueB) * orderNumber;
        }
        return (parseInt(valueA, 10) - parseInt(valueB, 10)) * orderNumber;
      }

      return (splitValuesA.length - splitValuesB.length) * orderNumber;
    }

    return (valueA - valueB) * orderNumber;
  });
}
